import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Waves, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1783980643230-69c54b9996a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhdWRpbyUyMGNvbnNvbGUlMjBkYXJrfGVufDB8fHx8MTc4NDk0ODMwOHww&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user =
        mode === "register"
          ? await register(name, email, password)
          : await login(email, password);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual panel */}
      <div className="relative hidden lg:block border-r border-zinc-800">
        <img src={BG} alt="Audio console" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-orange-600 flex items-center justify-center">
              <Waves className="h-6 w-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black tracking-tighter text-xl">LifeLike</span>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-4">
              Ed25519 Signed Licensing
            </p>
            <h2 className="text-4xl font-black tracking-tighter leading-tight max-w-md">
              Bespoke Q-SYS plugin licenses, verifiable online & offline.
            </h2>
            <p className="text-zinc-400 mt-4 max-w-md">
              Bind a signed license to your Locking ID. Try any plugin instantly with a
              self-service trial.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 grid-bg">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-9 w-9 rounded-sm bg-orange-600 flex items-center justify-center">
              <Waves className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black tracking-tighter text-lg">LifeLike License Portal</span>
          </div>

          <h1 className="text-3xl font-black tracking-tighter">
            {mode === "register" ? "Create account" : mode === "admin" ? "Admin console" : "Sign in"}
          </h1>
          <p className="text-zinc-400 text-sm mt-1 mb-8">
            {mode === "register"
              ? "Start trialing LifeLike plugins in seconds."
              : mode === "admin"
              ? "Sign in to manage plugins, licenses and customers."
              : "Access your licenses and trials."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="rounded-sm bg-zinc-900 border-zinc-800 focus-visible:ring-1 focus-visible:ring-orange-500"
                  placeholder="Jane Integrator"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm bg-zinc-900 border-zinc-800 focus-visible:ring-1 focus-visible:ring-orange-500"
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm bg-zinc-900 border-zinc-800 focus-visible:ring-1 focus-visible:ring-orange-500"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-red-400 font-mono">{error}</p>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white font-semibold"
            >
              {loading ? "..." : mode === "register" ? "Create account" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 space-y-3">
            {mode !== "admin" && (
              <button
                data-testid="auth-toggle-btn"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                }}
                className="block text-sm text-zinc-400 hover:text-orange-400 transition-colors"
              >
                {mode === "login"
                  ? "No account? Create one →"
                  : "Already registered? Sign in →"}
              </button>
            )}
            {mode === "admin" ? (
              <button
                data-testid="admin-exit-btn"
                onClick={() => { setMode("login"); setError(""); }}
                className="text-sm text-zinc-400 hover:text-orange-400 transition-colors"
              >
                ← Back to customer sign in
              </button>
            ) : (
              <button
                data-testid="admin-entry-btn"
                onClick={() => { setMode("admin"); setError(""); }}
                className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-orange-400 transition-colors"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Admin console sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
